import {inject} from "@angular/core";
import {
  capitalize,
  concat,
  extend,
  filter,
  flatten,
  forEach,
  get,
  groupBy,
  includes,
  isArray,
  isEmpty,
  keys,
  map,
  orderBy,
  set,
  sortBy,
  split,
  toLower,
  upperFirst
} from 'lodash-es';
import {
  extractType,
  getCanvasElement,
  getFieldDefProps,
  getMetaDataFromData,
  isDuplicateName,
  isEmptyObject,
  MarkupManagerService,
  prepareFieldDefs,
  prettifyLabel,
  resetObjectWithEmptyValues,
  WIDGET_CONSTANTS,
  WS_CONSTANTS
} from "@wms/core";
import {isObject, runtimeEventManager} from "@wm/core";
import {BaseComponent} from "@wms/components";
import {
  BaseVariableService,
  getVariableByName,
  getVariableConfig,
  isActionTypeVariable,
  retrieveEventCallbackVariables,
  VARIABLE_CONSTANTS
} from "@wms/variables";
import {DialogService, ProjectService, ProjectUpdatesService, TreeService} from "@wms/services";
import {PageMeta} from "@wms/app/services/tree/tree.service";
import { PageWsService } from "@wms/workspace";
import {ServiceFactoryService, TypeFactoryService} from "../services";
import {LiveWidgetUtilsService} from "../services/liveWidgetUtils/live-widget-utils.service";
import {EventManagerService} from "@wms/app";
import {VcsService} from "@wms/app/vcs/services/vcs.service";


export interface widgetOptions {
  name: string;
  id: string;
  category: string;
  type: string;
  variableCategory?: string;
}

const layoutWidgetTypes: { [key: string]: string } = {
  'wm-header': 'HEADER',
  'wm-footer': 'FOOTER',
  'wm-left-panel': 'LEFTNAV',
  'wm-right-panel': 'RIGHTNAV',
  'wm-top-nav': 'TOPNAV'
};

// TODO: replace with App level root
const $rs = {
  activePageName: '',
  isMobileApplicationType: false,
  isNativeMobileApplicationType: false,
  isTabletApplicationType: false
}
let projectDetails: any;
let projectService: ProjectService;
let markupManager: MarkupManagerService;
let typeUtils: TypeFactoryService;
let dataTypes: any;
let dialogService: DialogService
let treeService: TreeService;
let fieldTypeWidgetTypeMap: any;
let liveWidgetUtilsService: any;
let variablesService: BaseVariableService;
let serviceFactory: any;
let pageWSservice: PageWsService;
let eventManager: EventManagerService;
let projectUpdateService: ProjectUpdatesService;
let vcsService: VcsService;
export class StudioUtils extends BaseComponent {
  private markupManager: MarkupManagerService;
  private static locale: any;

  private pageWSservice: PageWsService;
  private activePageName: string = '';

  constructor() {
    super();
  }

  static init() {
    dialogService = inject(DialogService)
    markupManager = inject(MarkupManagerService);
    projectService = inject(ProjectService);
    liveWidgetUtilsService = inject(LiveWidgetUtilsService);
    variablesService = inject(BaseVariableService);
    serviceFactory = inject(ServiceFactoryService);
    typeUtils = inject(TypeFactoryService);
    treeService = inject(TreeService);
    pageWSservice = inject(PageWsService);
    $rs.activePageName = inject(PageWsService).activePageName;
    projectDetails = projectService.getDetails();
    fieldTypeWidgetTypeMap = liveWidgetUtilsService.getFieldTypeWidgetTypesMap();
    eventManager = inject(EventManagerService);
    projectUpdateService = inject(ProjectUpdatesService);
    vcsService = inject(VcsService);
  }

  static getContentOptions(widgetType: string) {
    let content: any = {
      'relevantOptions': [],
      'options': []
    },
      PAGE_COMMON = 'Common',
      pageTypesMapping = extend({}, layoutWidgetTypes, { 'wm-popover': 'POPOVER' });
    forEach(projectDetails.pages, (page: PageMeta) => {
      if (page.type === 'PAGE' || page.name === PAGE_COMMON) { // do not show the normal pages and Common partial page
        return;
      }
      if (pageTypesMapping[widgetType] === page.type) { //For layout widgets, show the relevant pages
        content.relevantOptions.push(page.name);
      }
      if ((page.type === 'PARTIAL') && (page.name !== pageWSservice.activePageName)) { //Show list of partial pages for page container widgets
        content.options.push(page.name);
      }
    });

    return content;
  }

  static sortObjectByKeys(obj: any) {
    let sortedKeys = sortBy(keys(obj)), sortedObj = {};
    forEach(sortedKeys, function (key) {
      set(sortedObj, key, obj[key])
    });
    return sortedObj;
  }

  static getVariableDetails(evtVal: string) {
    let variableType,
      variableCategory,
      matchStr: any;
    forEach(getVariableConfig(), function (option) {
      if (includes(evtVal, option.newVariableKey)) {
        matchStr = option.newVariableKey;
        variableType = option.collectionType;
        variableCategory = option.category;
      }
    });
    evtVal = evtVal.replace(matchStr, '${0}');
    return {
      'type': variableType,
      'category': variableCategory,
      'evtVal': evtVal
    };
  }



  static getVarExp(varName: string) {
    let varObj, nameWithMethod, nameSpace,
      methods: any = {
        'read': 'listRecords',
        'insert': 'createRecord',
        'update': 'updateRecord',
        'delete': 'deleteRecord'
      };
    if (includes(varName, '.')) {
      nameWithMethod = varName;
      varName = nameWithMethod.split('.')[0];
      varObj = getVariableByName(varName);
    }
    else {
      varObj = getVariableByName(varName);
      nameWithMethod = varName + '.' + (varObj.category === 'wm.LiveVariable' ? methods[varObj.operation] : 'invoke');
    }
    nameSpace = isActionTypeVariable(varObj) ? 'Actions' : 'Variables';
    return nameSpace + '.' + nameWithMethod + '()';

  }

  static getVariableName(varName: string) {
    return getVariableByName(varName);
  }

  static getUniqueWidgetName(markup: JQuery<HTMLElement>, name: string, count?: number): string {
    let _name, isUnique, suffix;
    suffix = count ? '_' + count : '';
    _name = name + suffix;
    isUnique = !markup.find('[name=' + _name + ']').length;

    if (isUnique) {
      return _name;
    }
    return getUniqueWidgetName(markup, name, (count || 0) + 1);
  }

  static getWidgetMethodOptions(markup: JQuery<HTMLElement> | undefined) {
    let widgetOptions: Array<widgetOptions> = [];
    if (!$rs.activePageName) {
      return;
    }
    if (!markup) {
      return;
    }
    // calendar exposed methods will not be shown for mobile projects.
    if (!$rs.isMobileApplicationType) {
      setWidgetOptions(widgetOptions, markup, 'wm-calendar', 'Calendar', ['gotoDate', 'gotoPrevYear', 'gotoNextYear', 'select', 'rerenderEvents']);
    }
    setWidgetOptions(widgetOptions, markup, 'wm-liveform', 'Live Form', ['cancel', 'delete', 'new', 'reset', 'save']);
    setWidgetOptions(widgetOptions, markup, 'wm-table', 'Data table', ['refreshData', 'redraw', 'clearFilter']);
    return widgetOptions;
  }

  static getDialogOptions(markup?: JQuery<HTMLElement> | undefined) {
    let dialogWidgets,
      dialogOptions: Array<widgetOptions> = [];
    if ($rs.activePageName) {
      if (!markup) {
        return;
      }
      // Find all dialogs except bottomsheet
      let dialogs = markup.find('wm-dialog, wm-confirmdialog, wm-alertdialog, wm-pagedialog, wm-iframedialog, wm-logindialog');
      // Find all bottomsheets
      let bottomsheets = markup.find('wm-bottomsheet');

      // Process regular dialogs
      forEach(dialogs, (dialog) => {
        let dialogId = $(dialog).attr('name') || '';
        /* not pushing dialog show/hide options for the same dialog*/
        if (dialogId !== 'notificationalertdialog' && dialogId !== 'notificationconfirmdialog') {
          dialogOptions.push(
            { name: dialogId + '.open', category: 'Dialogs', id: dialogId, type: 'WIDGET' },
            { name: dialogId + '.close', category: 'Dialogs', id: dialogId, type: 'WIDGET' }
          );
        }
      });

      // Process bottomsheets
      forEach(bottomsheets, (bottomsheet) => {
        let bottomsheetId = $(bottomsheet).attr('name') || '';
        dialogOptions.push(
          { name: bottomsheetId + '.open', category: 'Bottom Sheet', id: bottomsheetId, type: 'WIDGET' },
          { name: bottomsheetId + '.close', category: 'Bottom Sheet', id: bottomsheetId, type: 'WIDGET' }
        );
      });
    }
    return dialogOptions;
  }

  /**
   * extract the property name in the following format "methodName/className(package)"
   * @param val, passs property val
   * @returns {*}
   */
  static getUpdatedPropertyName(val: any, isEntityName?: any) {
    if (val.substring(0, val.lastIndexOf('.'))) {
      return isEntityName ? upperFirst(extractType(val)) : extractType(val) + '(' + val.substring(0, val.lastIndexOf('.')) + ')';
    }
    else {
      return val;
    }
  }

  /**
   * @ngdoc function
   * @name wmCore.$StudioUtils#getFilteredOptions
   * @methodOf wmCore.$StudioUtils
   * @function
   *
   * @description
   * Method to get filtered results
   */
  static getFilteredOptions(eventOptions: string[], search?: any, groupby?: any, compareBy?: any) {
    let filtered, results;
    groupby = groupby || 'category';
    compareBy = compareBy || 'name';
    search = search ? search.toLowerCase() : '';
    eventOptions = eventOptions || [];
    if (!search) {
      return eventOptions;
    }
    if (isObject(eventOptions[0])) {
      filtered = filter(eventOptions, (option) => {
        const compareVal = option?.[compareBy]?.toLowerCase?.() || '';
        const groupVal = option?.[groupby]?.toLowerCase?.() || '';
        // option.firstInGroup=undefined;
        return option && (includes(compareVal, search) || includes(groupVal, search));
      });


      results = flatten(map(sortObjectByKeys(groupBy(filtered, groupby)), function (g: any) {
        g = sortBy(g, (i) => {
          return toLower(i.name);
        });
        if (g[0] && isObject(g[0])) {
          g[0].firstInGroup = true;
        }
        return g;
      }));
    }
    else {
      results = filter(eventOptions, (option) => {
        return option && includes(option.toLowerCase(), search);
      });

      results = flatten(map(sortObjectByKeys(groupBy(results, groupby)), function (g: any) {
        g = sortBy(g, (i) => {
          return toLower(i.name);
        });
        if (g[0] && isObject(g[0])) {
          g[0].firstInGroup = true;
        }
        return g;
      }));
    }
    return results;
  }

  static redirectToLoginPage() {
    let locationHash = location.pathname,
      currentPageName = this.getCurrentPage();

    /* store the reference url in local storage*/
    // browserStorage.storeItem('wm.referenceUrlToStudio', currentPageName + '#' + locationHash);

    window.location.href = "login.html";
  }

  static redirectToIndexPage(path: any) {
    let indexpage = this.getIndexPage()
    if (!path) {
      window.location.href = indexpage;
    } else {
      window.location.href = indexpage + '#' + path;
    }
  }

  static getCurrentPage() {
    var pathName = location.pathname;
    return pathName.split('/').pop() || 'index.html';
  }

  /*function to find if executing in debug mode or normal mode*/
  static isDebugMode() {
    return this.getCurrentPage().indexOf('-debug') !== -1;
  }

  /*function get current Page*/
  static getIndexPage() {
    return this.isDebugMode() ? 'index-debug.html' : 'index.html';
  }



  static getVariableOptions(variableName?: string, context?: any) {
    // TODO: Add this method on variables
    let variableList = retrieveEventCallbackVariables(variableName, context)
    let options: Array<widgetOptions> = [];
    forEach(variableList, (obj: any) => {
      // TODO get($rs, 'locale')?.LABEL_VARIABLE_SERVICETYPE?[obj.serviceType]
      let serviceType = '';
      let locale = runtimeEventManager.getService('i18Services').getMessages();
      if (isEmpty(obj.methods)) {
        options.push({
          'name': obj.name,
          'category': locale.LABEL_VARIABLE_SERVICETYPE[obj.serviceType] || obj.serviceType || serviceType,
          'variableCategory': obj.category,
          'type': 'VARIABLE',
          'id': obj.name
        });
      } else {
        forEach(obj.methods, function (method) {
          options.push({
            name: obj.name + '.' + method,
            category: locale.LABEL_VARIABLE_SERVICETYPE[obj.serviceType] || obj.serviceType,
            variableCategory: obj.category,
            type: 'VARIABLE',
            id: obj.name
          });
        });
      }
    });
    return options;
  }

  static getPrefabEventOptions(markup?: JQuery<HTMLElement> | any) {
    let prefabs: any = [],
      prefabEventOptions: any = [];
    if (!markup) {
      return;
    }
    if ($rs.activePageName) {
      markup.find('wm-prefab')
        .each((idx: any, prefab: any) => {
          prefabs.push('section[widgetid][name="' + $(prefab).attr('name') + '"]');
        });

      $(getCanvasElement())
        .find(prefabs.join(', '))
        .each((idx: any, prefab: any) => {
          let prefabEl = $(prefab)[0],
            prefabIScope = prefabEl.widget;
          if (prefabIScope._methodsMap) {
            prefabEventOptions = (Object.keys(prefabIScope._methodsMap).map((method: any) => {
              return { name: prefabIScope.name + '.' + method, id: prefabIScope.name, category: 'prefab', type: 'PREFAB' };
            }));
          }
        });
    }
    return prefabEventOptions;
  }

  // Generate dataFields if we select inner(child) node static variable as dataset while configuring form widget
  static generateDataFields(binddataset: any, dataFields: any) {
    let path;
    path = binddataset.replace(/^bind:Variables\..*\.dataSet([^.])*(\.|$)/g, "");
    if (path) {
      path = path.split('.');
      forEach(path, function (el) {
        el = el.replace('[$i]', '');
        dataFields = Array.isArray(dataFields[el]) ? dataFields[el][0] : dataFields[el];
      })
    }
    return dataFields;
  }

  // Generate fields from static variable data when type is not present for variable
  static generateFieldDefsForStaticData(dataFields: any, path?: any) {
    let fields: any = [];
    forEach(dataFields, (val, fieldName) => {
      let fieldDef: any;
      fieldDef = getFieldDefProps(fieldName);
      fieldDef.field = fieldName;
      fieldDef.widget = 'text';
      fieldDef.path = path ? path + '.' + fieldName : fieldName;
      fieldDef.pcDisplay = true;
      if (isObject(val) && !Array.isArray(val)) {
        fieldDef.isRelated = true;
        fieldDef.widget = 'select';
      }
      fields.push(fieldDef);
    });
    return fields;
  }

  // Generate fields from static variable data when type is not present for variable
  static generateFieldsFromStaticData(variable: any, paramInfo: any, binddataset: any) {
    let dataFields = Array.isArray(variable.dataSet) ? variable.dataSet[0] : variable.dataSet,
      column,
      fields;
    if (binddataset) {
      dataFields = StudioUtils.generateDataFields(binddataset, dataFields);
    }

    fields = StudioUtils.generateFieldDefsForStaticData(dataFields);
    column = {
      'title': paramInfo.name,
      'fields': fields,
      'isList': paramInfo.isList,
      'requireSwitch': false
    };
    return column;
  }

  //Get the fields for specified data type
  static getFormFieldsForType(variable: any, type: any, paramInfo: any, binddataset?: any) {
    let column: any,
      relatedVariable,
      listIndex,
      listFields: any = [],
      variableFilter = typeUtils.getTypes()[type];
    if (!variableFilter) {
      if (variable.category !== 'wm.Variable') {
        return {};
      }
      return StudioUtils.generateFieldsFromStaticData(variable, paramInfo, binddataset);
    }

    column = {
      'title': paramInfo.name,
      'fields': StudioUtils.generateFieldDefsForService(variableFilter.fields, paramInfo.name),
      'isList': paramInfo.isList,
      'requireSwitch': false
    };
    forEach(column.fields, (field: any) => {
      if (field.isList) {
        relatedVariable = variablesService.filterByVariableKeys({
          'package': field.type
        })[0];
        field.fields = relatedVariable && relatedVariable.propertiesMap.columns;
        listFields.push(field);
      }
    });
    forEach(listFields, (field) => {
      listIndex = column.fields.indexOf(field);
      column.fields.splice(listIndex, 1);
    });
    column.fields = concat(column.fields, listFields);

    return column;
  }

  static getTypeModel(type: any) {
    let model = {},
      serviceObj,
      serviceType;
    if (type) {
      serviceObj = serviceFactory.getServiceObjectByName(get(typeUtils.getTypes(), [type, 'service']));
      serviceType = serviceObj && serviceObj.type;
    }
    this.prepareTypeModel(type, model, undefined, serviceType);
    return model;
  }

  static prepareTypeModel(type: any, parentNode: any, typeChainArr: any, serviceType: any) {
    /* case when the data returned from the service is not an object */
    if (!typeUtils.getTypes()[type] || !typeUtils.getTypes()[type].fields || isEmptyObject(typeUtils.getTypes()[type].fields)) {
      parentNode['value'] = '';
      return;
    }

    /* if type already processed, return */
    if (!typeChainArr) {
      typeChainArr = [];
    }
    if (typeChainArr.indexOf(type) !== -1) {
      return;
    }

    typeChainArr.push(type);
    forEach(typeUtils.getTypes()[type].fields, (field, fieldName) => {
      /* if the field is of type list and variable is not a service variable, skip it.
       * skipping as it is resulting in endless recursive loop for DataServices
       */
      if (serviceType !== VARIABLE_CONSTANTS.SERVICE_TYPE_DATA || !this.isListAndNonBlobType(field)) {
        if (typeUtils.getTypes()[field.type] && typeUtils.getTypes()[field.type].fields) {
          parentNode[fieldName] = field.isList ? [{}] : {};
          this.prepareTypeModel(field.type, field.isList ? parentNode[fieldName][0] : parentNode[fieldName], typeChainArr, serviceType);
        } else {
          parentNode[fieldName] = field.isList ? [] : '';
        }
      }
    });
  }

  //Ignore list type fields for dataServices(Except blob columns)
  static isListAndNonBlobType(field: any) {
    return field.isList && toLower(extractType(field.type)) !== 'byte';
  }

  //Return primitive type(lower case) for the type given
  static getPrimitiveType(type: any) {
    return toLower(get(typeUtils.getTypes(), [type, 'primitiveType']));
  }

  // Generate fields for service variable from datatypes
  static generateFieldDefsForService(propertiesConfig: any, title?: any) {
    let properties = resetObjectWithEmptyValues(propertiesConfig),
      fields;
    fields = orderBy(prepareFieldDefs(properties), ['field'], ['asc']);
    forEach(fields, (field: any) => {
      let configType, fieldPrimitiveType;
      field.columnName = title ? (title + '.' + field.field) : field.field;
      configType = propertiesConfig[field.field].type;
      field.configType = configType;
      field.isRelated = typeUtils.getTypes()[configType] && !isEmpty(typeUtils.getTypes()[configType].fields);
      //Static variable and service variable doesn't get is related on field so if its not primitive make it list type
      field.type = StudioUtils.getPrimitiveType(configType) ? configType : 'list';
      fieldPrimitiveType = extractType(field.type) || 'list';
      //If there is no matching fieldTypeWidget it means its not a primitive type and widget should be select by default
      field.widget = get(fieldTypeWidgetTypeMap, fieldPrimitiveType + '[0]');
      field.isList = propertiesConfig[field.field].isList;
      field.pcDisplay = field.pcDisplay || true;
    });
    return fields;
  }

  // For a related field, get the inner fields info
  static getInnerFieldDefs(variable: any, field: any, path: any, binddataset: any) {
    // For static variable with no type, get fields info from data
    if (variable.category === 'wm.Variable' && !field.configType) {
      let dataFields = Array.isArray(variable.dataSet) ? variable.dataSet[0] : variable.dataSet;
      if (binddataset) {
        dataFields = StudioUtils.generateDataFields(binddataset, dataFields);
      }
      dataFields = get(dataFields, split(path, '.'));
      field.fields = StudioUtils.generateFieldDefsForStaticData(dataFields, path);
    } else if (field.configType) {
      field.fields = StudioUtils.generateFieldDefsForService(typeUtils.getTypes()[field.configType].fields);
    }
  }

  /**
 * @description
 * Method to get the primitive types and columns for a variable
 */
  static getPrimitiveFieldsForType(variable: any, binddataset?: any) {
    let params,
      isGetOperation: any,
      paramWidget,
      columns: any = [],
      primitiveParams: any = [],
      method = 'post',
      column;

    /**
     * Adds the primitive param to the primitiveParams Array
     * @param param
     */
    const addPrimitiveParam = (param: any) => {
      /**
       * get widget type from widgetTypeMap
       * (1) check for param.format or param.typeRef
       * (2) if (1) doesn't retrieve widget then map it to text by default.
       */
      paramWidget = get(fieldTypeWidgetTypeMap, '[' + param.format + '][0]') || get(fieldTypeWidgetTypeMap, '[' + param.typeRef + '][0]')
        || fieldTypeWidgetTypeMap.text[0];
      primitiveParams.push(
        {
          'displayName': prettifyLabel(param.name),
          'type': param.typeRef,
          'field': param.name,
          'isList': param.isList,
          'widget': paramWidget,
          'pcDisplay': true,
          'mobileDisplay': true
        }
      );
    }
    let serviceOperationInfo = { httpMethod: 'get' };
    let primitiveFields: any;
    switch (variable.category) {
      //TODO: check on $servicevariable

      case 'wm.ServiceVariable':
        // $servicevariable.getServiceOperationInfo(variable.operation, variable.service, (serviceOperationInfo: any) => {
        isGetOperation = serviceOperationInfo?.httpMethod === 'get';
        primitiveFields = serviceFactory.getServiceOperationParams(variable.service, variable.operationId).then((response: any) => {
          params = response.parameter;
          method = response.operationType;
          if (isEmpty(params)) {
            return;
          }
          forEach(params, (param) => {
            let isParamPrimitiveType;
            switch (param.parameterType) {
              case 'query':
              case VARIABLE_CONSTANTS.BODY_FIELD: //bodyField is the paramType for the BodyTypeQueryProcedure
                /**
                 * Verify if the Param is of primitive type and
                 * 1. If variable has get operation, add the params but verify they're not of pagination type
                 * 2. If variable is not of get operation then by default add them
                 */
                if (!isGetOperation || !includes(VARIABLE_CONSTANTS.PAGINATION_PARAMS, param.name)) {
                  addPrimitiveParam(param);
                }
                break;
              case 'formData':
                isParamPrimitiveType = includes(WS_CONSTANTS.PRIMITIVE_DATA_TYPES, param.typeRef) || includes(keys(fieldTypeWidgetTypeMap), param.typeRef);
                if (isParamPrimitiveType) {
                  addPrimitiveParam(param);
                } else {
                  columns.push(StudioUtils.getFormFieldsForType(variable, param.typeRef, param));
                }
                break;
              case 'body':
                columns.push(StudioUtils.getFormFieldsForType(variable, param.typeRef, param));
                break;
            }
          });
          return Promise.resolve({
            'columns': columns,
            'primitiveParams': primitiveParams,
            'method': method
          });
        });
        break;
      case 'wm.CrudVariable':
        // $servicevariable.getServiceOperationInfo(variable.operation, variable.service, (serviceOperationInfo: any) => {
        isGetOperation = serviceOperationInfo.httpMethod === 'get';
        primitiveFields = serviceFactory.getCrudServiceOperationParams(variable.service, variable.crudOperationId, 'createorupdate').then((response: any) => {
          params = response.parameter;
          method = response.operationType ? response.operationType :method;
          if (isEmpty(params)) {
            return;
          }
          forEach(params, (param) => {
            let isParamPrimitiveType;
            switch (param.parameterType) {
              case 'query':
              case VARIABLE_CONSTANTS.BODY_FIELD: //bodyField is the paramType for the BodyTypeQueryProcedure
                /**
                 * Verify if the Param is of primitive type and
                 * 1. If variable has get operation, add the params but verify they're not of pagination type
                 * 2. If variable is not of get operation then by default add them
                 */
                if (!isGetOperation || !includes(VARIABLE_CONSTANTS.PAGINATION_PARAMS, param.name)) {
                  addPrimitiveParam(param);
                }
                break;
              case 'formData':
                isParamPrimitiveType = includes(WS_CONSTANTS.PRIMITIVE_DATA_TYPES, param.typeRef) || includes(keys(fieldTypeWidgetTypeMap), param.typeRef);
                if (isParamPrimitiveType) {
                  addPrimitiveParam(param);
                } else {
                  columns.push(StudioUtils.getFormFieldsForType(variable, param.typeRef, param));
                }
                break;
              case 'body':
                columns.push(StudioUtils.getFormFieldsForType(variable, param.typeRef, param));
                break;
            }
          });
          return Promise.resolve({
            'columns': columns,
            'primitiveParams': primitiveParams,
            'method': method
          });
        });
        break;

      case 'wm.Variable':
        column = StudioUtils.getFormFieldsForType(variable, variable.type, { 'name': '', 'isList': variable.isList }, binddataset);
        primitiveParams = (column && column.fields) || [];
        primitiveFields = Promise.resolve({
          'columns': columns,
          'primitiveParams': primitiveParams,
          'method': method
        });
    }
    return primitiveFields ? primitiveFields : Promise.resolve();
  }

  static onPageOrTemplateCreateSuccess($s: any, pageName: string, addNewWorkspace?: any) {
    $s.fileAddMessage = null;
    eventManager.emitUpdatePageTree({pageName: pageName,  callBack : () => {runtimeEventManager.emit('loadPage', {pageName, isTemplate: get($s.template, 'selected.type') === 'template'})}});
    // eventManager.emitUpdateResourcesTree();

    // This method was updating the markup (setMarkupRoot) in MarkupManager before the page save is finished. Hence commented this below code.
    // eventManager.emitReRenderCanvasContent({ pageName: pageName, isMarkupUpdated: true });



    // eventManager.emitUpdatePageTree( function () {
    //   addNewWorkspace(pageName, $s.newPage.type);
    // });
    // eventManager.emitUpdateResourcesTree('update-resources-tree');

    // Sending GA event on successful creation of a page/partial/template
    // if ( $s.template && $s.template.selected ) { // duplicate page - template id won't be available. So, not sending event.
    //   $rs.$emit('wms:studio-event', {eventName: 'wm-create-page', eventVal: $s.newPage.type + ':' + $s.template.selected.id});
    // }
  }

  static addConfig($s: any, templateName: any, config: any) {
    if (!config) {
      return config;
    }

    if (!config.templates) {
      config.templates = [];
    }

    config.templates.push(
      {
        'id': templateName,
        'name': templateName,
        'description': templateName + ' template',
        'category': $s.template.category,
        'keywords': templateName,
        'published': true,
        'type': $s.newPage.type,
        'thumbnail': '',
        'screenshots': []
      }
    );
    return config;
  }
  static showMessage(messageKey: any) {
    var messageTitle = '',
      messageDesc = '',
      messageType = '';

    switch (messageKey) {
      case 'READ_CONFIG_ERROR':
        messageTitle = this.locale.TITLE_READ_CONFIG_ERROR || 'Read config Error';
        messageDesc = this.locale.MESSAGE_READ_CONFIG_ERROR || 'Error while reading the config file of template bundle';
        messageType = 'error';
        break;
      case 'WRITE_CONFIG_ERROR':
        messageTitle = this.locale.TITLE_WRITE_CONFIG_ERROR || 'Write config Error';
        messageDesc = this.locale.MESSAGE_WRITE_CONFIG_ERROR || 'Error while writing the config file of template bundle';
        messageType = 'error';
        break;
      case 'WRITE_CONFIG_SUCCESS':
        messageTitle = this.locale.TITLE_WRITE_CONFIG_SUCCESS || 'Success';
        messageDesc = this.locale.MESSAGE_WRITE_CONFIG_SUCCESS || 'Config of the template bundle successfully updated';
        messageType = 'success';
        break;
    }

    // ww.show(messageType, messageTitle, messageDesc);
  }

  static updateTemplateConfig(action: any, templateName: any, dialogId?: any, $s?: any) {

    // let fn;
    // let
    //
    // if (ACTIONS.ADD === action) {
    //   fn = StudioUtils.addConfig.bind(undefined, $s, templateName);
    // } else if (ACTIONS.DELETE === action) {
    //   fn = StudioUtils.removeConfig.bind(undefined, templateName);
    // }
    //
    // PageTemplatesService.getConfig()
    //   .then(fn, StudioUtils.showMessage.bind(undefined, 'READ_CONFIG_ERROR'))
    //   .then(StudioUtils.saveConfig.bind(undefined, dialogId));
  }
  // static saveConfig(dialogId:any, config:any, callback:any) {
  //   return PageTemplatesService.setConfig(config)
  //     .then((config:any)=> {
  //       StudioUtils.onSaveConfigSuccess(dialogId);
  //       triggerFn(callback);
  //     }, StudioUtils.showMessage.bind(undefined, 'WRITE_CONFIG_ERROR'));
  // }

  static onPageCreationSuccess(pageName: string, dialogId: any, $s: any, action: any, dataset: any) {
    StudioUtils.onPageOrTemplateCreateSuccess($s, pageName);
    if ($s.newPage && toLower($s.newPage.type) === 'page') {
      StudioUtils.updateAppVariables(pageName, action, dataset);
    }
    $s.templateCreated = true;
    dialogService.close(dialogId)

  }
  static updateAppVariables(pageName: string, action: any, dataset: any) {
    let variableOptions: any = {
      name: 'goToPage_' + pageName,
      owner: 'App',
      operation: 'gotoPage',
      pageName: pageName
    },
      createdVariable: any,
      variableName,
      variableOwner = 'App',
      category = 'wm.NavigationVariable';

    if (action === 'DUPLICATE') {
      variableOptions.dataSet = dataset;
    }

    /*Creating Navigation variable along with page creation*/
    createdVariable = variablesService.createVariable(category, variableOptions, variableOptions.name);
    variableName = createdVariable.name;

    /* Transform variable to the format required by variable service */

    createdVariable.category = category;

    /* Store the variable in collection */
    variablesService.store(variableOwner, variableName, createdVariable);
    variablesService.saveVariables(pageName);

    /*Update the tree with the newly added variable under the appropriate category*/
    eventManager.emitUpdateVariableTree(createdVariable);
  }

  static getFieldName(prop: any) {
    let propField = prop.field.split('.'),
      fieldName = '';
    forEach(propField, function (name) {
      fieldName += includes(name, ' ') ? "['" + name + "']" : '.' + name;
    });
    return fieldName;
  }


  static getFieldObjectsForData(data: any, options?: any) {
    options = options || {};

    var defs: any[] = [],
      prefix = options.prefix || '',
      subDefs,
      dataObject;

    dataObject = getMetaDataFromData(data);

    // if data is not an object, then do not generate the options.
    if (!isObject(dataObject)) {
      return defs;
    }

    forEach(dataObject, function (val, key) {
      if (isObject(val) && !isEmpty(val)) {
        defs.push({
          'field': prefix + key,
          'type': 'object'
        });
        if (isArray(val)) {
          options.prefix = prefix + key + '[0].';
          subDefs = StudioUtils.getFieldObjectsForData(val[0], options);
        } else {
          options.prefix = prefix + key + '.';
          subDefs = StudioUtils.getFieldObjectsForData(val, options);
        }
        defs = defs.concat(subDefs);
      } else {
        defs.push({
          'field': prefix + key,
          'type': 'terminal'
        });
      }
    });

    return defs;
  }



  static focusPageNameInput() {
    $('.add-new-page-container-dialog [name="wm-new-page-name"]').focus();
  }
  static validatePageName($s: any, pageName: any) {
    var pageNameRegEx = /^[\w-]+$/,
        numberRegEx = /^\d/,

      projectPages = projectService.getPageNames();

    $s.fileAddMessage = null;

    if (!pageName) {
      $s.fileAddMessage = { 'caption': $s.locale.MESSAGE_WARN_PAGE_NAME_EMPTY_DESC, type: 'error' };
      StudioUtils.focusPageNameInput();
      return false;
    }

    if (numberRegEx.test(pageName)) {
      $s.fileAddMessage = { 'caption': $s.locale.MESSAGE_ERROR_PAGE_NAME_INVALID_NUMBER_DESC, type: 'error' };
      StudioUtils.focusPageNameInput();
      return false;
    }

    if (!pageNameRegEx.test(pageName)) {
      $s.fileAddMessage = { 'caption': $s.locale.MESSAGE_ERROR_PAGE_NAME_INVALID_DESC, type: 'error' };
      StudioUtils.focusPageNameInput();
      return false;
    }

    if (isDuplicateName(projectPages, pageName, true)) {
      $s.fileAddMessage = { 'caption': $s.locale.MESSAGE_ERROR_PAGE_NAME_EXISTS_DESC, type: 'error' };
      StudioUtils.focusPageNameInput();
      return false;
    }

    return true;

  }
}

/**
 * Get unique name for widget by checking the current markup
 * @param markup - current editor markup
 * @param name - name of the widget
 * @param count - count to get unique identifier
 */
export const getUniqueWidgetName = (markup: JQuery<HTMLElement> | undefined, name: string, count?: number): string => {
  let _name, isUnique, suffix;
  suffix = count ? '_' + count : '';
  _name = name + suffix;
  isUnique = !markup?.find('[name=' + _name + ']').length;

  if (isUnique) {
    return _name;
  }
  return getUniqueWidgetName(markup, name, (count || 0) + 1);
}

const setWidgetOptions = (widgetOptions: Array<widgetOptions>, markup: JQuery<HTMLElement>, widgetId: string, widgetCategory: string, methods: Array<string>) => {
  let widgets = markup.find(widgetId);
  // find all the dialogs on the page to show the events related to them in properties panel
  forEach(widgets, (el) => {
    let widgetName = $(el).attr('name') || '';
    forEach(methods, (method) => {
      widgetOptions.push({
        name: widgetName + '.' + method,
        id: widgetName,
        category: widgetCategory,
        type: 'WIDGET'
      });
    });
  });
}

// returns the object sorted by keys
export function sortObjectByKeys(obj: any) {
  let sortedKeys = sortBy(keys(obj));



  //Fix: Move 'default' category to the top of the list to ensure it appears before other category options.
  const defaultIndex = sortedKeys.indexOf('Default');
  if (defaultIndex !== -1) {
    sortedKeys.splice(defaultIndex, 1);
    sortedKeys.unshift('Default');
  }
  let sortedObj = {};
  forEach(sortedKeys, (key) => {
    (sortedObj as any)[key] = obj[key];
  });

  return sortedObj;
}

export function processLiveWidgetTemplate(template: any, $s: any) {
  let parsedTemplate = new DOMParser().parseFromString(template, 'text/html');
  console.log('I have been parsed', parsedTemplate);
  // template = template.elementRef.nativeElement;
  // template.find("[data-is-target]") // TODO: get the element
  let targetWidgets = parsedTemplate.querySelectorAll("[data-is-target]"),
    widgets: any[] = [],
    currentWidget: { name: any; aspectRatio: any; hasBinding?: any; widgetProps: any; type?: any; };
  console.log('target widgets here', targetWidgets);
  forEach(targetWidgets, (widget: any) => {
    let existingWidget: any;
    currentWidget = {
      "name": widget.getAttribute('name'),
      "type": widget.tagName.toLowerCase(),
      "widgetProps": [],
      "aspectRatio": ''
    };
    existingWidget = $s.templateWidgets && $s.templateWidgets.filter((widget: any) => { return widget.name === currentWidget.name; })[0];
    if (includes(['wm-card', 'wm-picture'], widget.tagName.toLowerCase()) && widget.getAttribute("aspect-ratio")) {
      currentWidget.aspectRatio = ' (' + widget.getAttribute("aspect-ratio") + ')';
    }
    widgets.push(currentWidget);
    let attributes = widget.attributes;
    forEach(attributes, (attr) => {
      if (attr.nodeValue === "{}") {
        let existingAttr = get(existingWidget, 'widgetProps') && existingWidget.widgetProps.filter((property: any) => { return property.name === attr.nodeName })[0],
          attrObj: any = {
            'name': attr.nodeName
          };
        if (existingAttr) {
          currentWidget.hasBinding = existingWidget.hasBinding;
          attrObj.value = existingAttr.value;
          attrObj.field = existingAttr.field;
        } else {
          attrObj.value = '';
        }
        currentWidget.widgetProps.push(attrObj);
      }
    });
  });

  $s.templateWidgets = widgets;
  $s.selectTemplateWidget(widgets[0]);

}

export function getFieldName(prop: any) {
  let propField = prop.field.split('.'),
    fieldName = '';
  forEach(propField, (name) => {
    fieldName += includes(name, ' ') ? "['" + name + "']" : '.' + name;
  });
  return fieldName;
}

// Function return the studio url.
export function getStudioUrl(): any {
  let locationObj = window.location;
  return locationObj.origin + (locationObj.host.indexOf(':8080') > -1 ? '/wavemaker' : '/studio');
}

/**
 * Get fields required for widgetConfigDialog
 * @param data - Fields objects fromTypeUtils
 */
export function getFieldsForService(data: any) {
  let getModifiedName = (name: string) => {
    if (includes(name, '.')) {
      let lastIndex = name.lastIndexOf('.');
      return capitalize(name.substring(0, lastIndex)) + ' ' + prettifyLabel(name.substring(lastIndex + 1));
    }
    return prettifyLabel(name);
  };
  return map(data, (val) => {
    let modifiedName = getModifiedName(val?.field);
    return {
      'displayName': modifiedName,
      'field': val?.field,
      'relatedField': modifiedName,
      'type': val?.dataType
    };
  });
}

export function onAccessRolesChange(property: any, nv: any, callback?: any) {
  var UNICODE_COMMA = '&#44;',
    retVal;

  nv = nv || [];
  retVal = nv;
  if (isArray(nv)) {
    // replace commas in role with unicode equivalent
    nv = nv.map(function (v) {
      return v.replace(/,/g, UNICODE_COMMA);
    });
    retVal = nv.join(',');
  }
  if (callback) {
    return Promise.resolve({property, retVal});
  } else {
    return retVal;
  }
}

export  function getNewEventsObject(prefix: any, events: any, args: any) {
  var newEventName,
    newCustomEvent,
    eventNumber = 0,
    customEvents: any[] = [];
  forEach(events, function (event, index) {
    if (event === WIDGET_CONSTANTS.EVENTS.JAVASCRIPT) {
      newCustomEvent = prefix;
      newEventName = newCustomEvent + args;
      while (includes(events, newEventName)) {
        eventNumber += 1;
        newCustomEvent = prefix + eventNumber;
        newEventName = newCustomEvent + args;
      }
      events[index] = newEventName;
      customEvents = customEvents.concat(newCustomEvent);
    }
  });
  return {
    'events' : events,
    'customEvents' : customEvents
  };
}

export function handleInvalidRepoBranch() {
  const message = projectUpdateService?.invalidRepoBranchInfo;

  if (message === 'REPOSITORY_NOT_FOUND') {
    dialogService.open('missingRepoDialog');
    return true;
  }

  if (message === 'BRANCH_NOT_FOUND') {
    dialogService.open('missingBranchDialog');
    return true;
  }

  if (message === 'USER_VCS_CREDENTIALS_REQUIRED') {
    if (projectService.projectEdnDetails.vcsAccountDTO.developerKeysEnabled) {
      vcsService.showUserVCSCredentialsDialog('', 'USER_VCS_CREDENTIALS_REQUIRED');
    }
    else {
      dialogService.open('missingCredentialsDialog');
    }
    return true;
  }

  return false;
}

export function getVcsAccountIconUrl(vcsType: string, vcsUrl: string) {
  let providerIcon;
  switch(vcsType) {
    case 'AZURE':
      providerIcon = `${vcsUrl}/images/vcs-provider/AzureRepos.svg`;
      break;
    case 'BITBUCKET':
      providerIcon = `${vcsUrl}/images/vcs-provider/BitBucket.svg`;
      break;
    case 'GIT_LAB':
      providerIcon = `${vcsUrl}/images/vcs-provider/GitLab.svg`;
      break;
    case 'GIT_HUB':
    case 'GITHUB_SERVER':
      providerIcon = `${vcsUrl}/images/vcs-provider/GitHub.svg`;
      break;
    default:
      providerIcon = '';
  }
  return providerIcon;
}
export const accessTokenLink = {
  'AZURE'   : 'https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate?view=azure-devops&tabs=Windows',
  'GIT_LAB' : 'https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html#create-a-personal-access-token',
  'GIT_HUB' : 'https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token'
};

export function getVCSGroupData(vcsAccountType: string, properties: any) {
  let locale = runtimeEventManager.getService('i18Services').getMessages();
  let groupData = {
    groupName: '',
    groupLabel: '',
    accessTokenLink: ''
  };
  switch(vcsAccountType) {
    case 'AZURE':
      return {
        groupName: properties?.AZURE_PROJECT_NAME || '',
        groupLabel: locale.LABEL_PROJECT,
        accessTokenLink: accessTokenLink.AZURE
      };
    case 'BITBUCKET':
      return {
        groupName: properties?.BITBUCKET_PROJECT_NAME || '',
        groupLabel: locale.LABEL_PROJECT,
        accessTokenLink: ''
      };
    case 'GIT_LAB':
      return {
        groupName: properties?.GITLAB_GROUP_NAME || '',
        groupLabel: locale.LABEL_GROUP,
        accessTokenLink: accessTokenLink.GIT_LAB
      };
    case 'GIT_HUB':
    case 'GITHUB_SERVER':
      return {
        groupName: properties?.GITHUB_ORGANIZATION_NAME || '',
        groupLabel: locale.LABEL_ORGANIZATION,
        accessTokenLink: accessTokenLink.GIT_HUB
      };
    default:
      return groupData;
  }
}

export function toPascalCase(s: string): string {
  return s
    .toLowerCase()
    .split('_')
    .map(w => w[0].toUpperCase() + w.slice(1))
    .join('');
}

